import {Box, Center, Text, VStack} from '@chakra-ui/react';
import {State} from '@synako/halitrek';
import {MessageDesc} from 'api-types/ffconnect-hal';
import {format} from 'date-fns';
import {formatDateForDisplay} from 'ff-common';
import {useCallback, useEffect} from 'react';
import {ConversationsMessageBubble} from './ConversationsMessageBubble';

/**
 * Composant pour afficher la liste des messages avec scroll automatique.
 * Séparé pour permettre l'utilisation de useEffect proprement.
 *
 * @param isClientView - Si true, les messages contact_client sont à droite (vue client)
 */
export const ConversationMessages: React.FC<{
  messageStates: State<MessageDesc>[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  emptyMessage: string;
  currentCodeUtilisateur?: string | undefined;
  isClientView?: boolean;
  footerMessage?: string;
}> = ({
  messageStates,
  currentCodeUtilisateur,
  containerRef,
  emptyMessage,
  isClientView = false,
  footerMessage,
}) => {
  // Fonction pour scroller vers le bas
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [containerRef]);

  // Scroll automatique vers le bas quand les messages changent
  useEffect(() => {
    scrollToBottom();
  }, [messageStates.length, scrollToBottom]);

  if (messageStates.length === 0) {
    return (
      <Center h='100%'>
        <Text color='gray.dark'>{emptyMessage}</Text>
      </Center>
    );
  }

  // Grouper les messages par date
  let lastDate = '';

  return (
    <VStack
      align='stretch'
      spacing={0}
    >
      {messageStates.map((messageState) => {
        const messageDate = format(
          new Date(messageState.data.envoye_le),
          'yyyy-MM-dd',
        );
        const showDateSeparator = messageDate !== lastDate;
        lastDate = messageDate;

        return (
          <Box key={messageState.data.id}>
            {/* Séparateur de date */}
            {showDateSeparator && (
              <Box
                w='100%'
                display='flex'
                justifyContent='center'
                my={2}
              >
                <Text
                  fontSize='12px'
                  fontWeight='500'
                  color='gray.dark'
                  bg='white'
                  px={2}
                  py={1}
                  marginBottom={2}
                  borderRadius='8px'
                >
                  {formatDateForDisplay(
                    messageState.data.envoye_le,
                    'd MMMM yyyy',
                  )}
                </Text>
              </Box>
            )}
            {/* Bulle de message */}
            <ConversationsMessageBubble
              messageState={messageState}
              currentCodeUtilisateur={currentCodeUtilisateur}
              isClientView={isClientView}
              onImageLoad={scrollToBottom} // Scroll quand une image est chargée
            />
          </Box>
        );
      })}
      {/* Message en bas du fil des messages (ex: conversation clôturée) */}
      {footerMessage && (
        <Box
          w='100%'
          display='flex'
          justifyContent='center'
          py={4}
          px={6}
        >
          <Text
            fontSize='14px'
            color='gray.dark'
            textAlign='center'
            fontStyle='italic'
          >
            {footerMessage}
          </Text>
        </Box>
      )}
    </VStack>
  );
};
